import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { addProviderProps, providerDetailsProps } from './llm.props';
import BasicModal from '../../../../components/action-modal';
import {
  AddProviderDesc,
  AddProviderText,
  PROVIDER_SELECT_OPTIONS,
  ProviderKeyText,
  ProviderNameText,
} from './data/constants';
import { LLMIcon } from '../../../../assets/images';
import { createProvider } from '../services/llm';
import makeToast from '../../../../components/toaster';
import { FilterDropdown } from '../../../../components';
import { validateTextField } from '../../../../services';
import { reloadUserConfigurationsAfterLlmChange } from '../../user/services';
import Logout from '../../../../auth/protected/session-expire-modal';

function AddProvider({
  openProviderModal,
  setOpenProviderModal,
  setDataUpdated,
}: addProviderProps) {
  const [logout, setLogout] = useState(false);
  const [providerDetails, setProviderDetails] = useState<providerDetailsProps>({
    name: '',
    key: '',
  });
  const [errorMsgs, setErrorsMsgs] = useState<providerDetailsProps>({
    name: '',
    key: '',
  });

  const handleCancelClick = () => {
    setOpenProviderModal(false);
    setProviderDetails({
      name: '',
      key: '',
    });
    setErrorsMsgs({
      name: '',
      key: '',
    });
  };

  const handleDataChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    const error =
      value === ''
        ? `${name} is required`
        : validateTextField(value, name === 'name' ? 'Provider Name' : 'API Key');
    setErrorsMsgs({ ...errorMsgs, [name]: error });
    setProviderDetails({ ...providerDetails, [name]: value });
  };

  const handleSaveProvider = () => {
    const { name, key } = providerDetails;
    const nameError = validateTextField(name, 'Provider Name');
    const keyError = validateTextField(key, 'API Key');

    if (nameError || keyError) {
      setErrorsMsgs({ name: nameError, key: keyError });
      return;
    }

    createProvider(providerDetails)
      .then((res) => {
        if (res.status === 201) {
          setProviderDetails({
            name: '',
            key: '',
          });
          setOpenProviderModal(false);
          setDataUpdated((prev) => !prev);
          reloadUserConfigurationsAfterLlmChange();
          makeToast('success', 'Provider has been created!');
        } else if (res.status === 401) {
          setLogout(true);
        }
      })
      .catch((err) => {
        makeToast(
          'error',
          err?.response?.data?.message
            ? `${err?.response?.data?.message}.`
            : 'Something went wrong!',
        );
      });
  };

  return (
    <BasicModal
      className="w-[550px]"
      open={openProviderModal}
      confirmButtonText="Save"
      modalTitle={AddProviderText}
      modalDesc={AddProviderDesc}
      modalLogo={LLMIcon}
      resetData={handleCancelClick}
      saveData={handleSaveProvider}
    >
      <div className="flex flex-col text-sm">
        <span className="text-[#344054] font-medium">
          {ProviderNameText}
          <span className="text-red-600">*</span>
        </span>
        <FilterDropdown
          options={PROVIDER_SELECT_OPTIONS.map((option) => ({
            value: option,
            label: option,
          }))}
          value={providerDetails?.name ?? ''}
          onChange={(v: string) =>
            handleDataChange({
              target: { name: 'name', value: v },
            } as React.ChangeEvent<HTMLInputElement>)
          }
          placeholder="Select provider"
          showClear={false}
          className={`mt-1 w-full px-3 py-2 text-left flex items-center justify-between rounded-lg border bg-white ${
            errorMsgs.name !== ''
              ? 'error-msg border-red-600 focus:ring-red-600'
              : 'border-[#E4E4E7]'
          }`}
        />
        {errorMsgs.name && <span className="text-red-600">{errorMsgs.name}</span>}
      </div>
      <div className="flex flex-col text-sm">
        <span className="text-[#344054] font-medium">
          {ProviderKeyText}
          <span className="text-red-600">*</span>
        </span>
        <input
          className={`mt-1 ${
            errorMsgs.key !== '' && 'error-msg focus:border-red-600 border-red-600'
          } border border-[#E4E4E7] rounded-lg`}
          value={providerDetails?.key}
          name="key"
          required
          placeholder="Enter API key"
          onChange={handleDataChange}
        />
        {errorMsgs.key && <span className="text-red-600">{errorMsgs.key}</span>}
      </div>
      {logout && createPortal(<Logout />, document.body)}
    </BasicModal>
  );
}

export default AddProvider;
